import React, { useState } from 'react';

import {
  useController,
  UseControllerProps,
  FieldValues,
  Path,
  PathValue,
} from 'react-hook-form';
import {
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  TextFieldProps,
} from '@mui/material';

import { EditorInput, EditorInputProps } from './EditorInput/EditorInput';
import { useTipTapEditor } from '../../hooks/useTipTapEditor';

export type RichTextEditorProps<
  T extends FieldValues,
  TName extends Path<T>,
> = UseControllerProps<T, TName> & Pick<TextFieldProps, 'label' | 'size' | 'className'>;

const RichTextEditor = <T extends FieldValues, TName extends Path<T>>({
  control,
  name,
  rules,
  defaultValue = '' as PathValue<T, TName>,
  shouldUnregister,
  label,
  size = 'small',
  className,
}: RichTextEditorProps<T, TName>) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController<any>({
    control,
    name,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const [isFocused, setIsFocused] = useState(false);

  const onFocus = () => setIsFocused(true);
  const onBlur = () => setIsFocused(false);

  const { editor } = useTipTapEditor({
    value,
    size,
    onChange,
    onFocus,
    onBlur,
  });

  return (
    <FormControl
      className={className}
      error={!!error}
      fullWidth
      variant="standard"
      size={size}
      required={!!rules?.required}
    >
      {!!label && <InputLabel shrink={isFocused || !!value}>{label}</InputLabel>}
      <Input
        slots={{
          input: EditorInput,
        }}
        slotProps={{
          input: {
            editor,
          } as EditorInputProps,
        }}
      />
      <FormHelperText>{error?.message || ' '}</FormHelperText>
    </FormControl>
  );
};

export { RichTextEditor };
